import type { AppSettings, PersistedSettings } from './types.js'

const KEY = 'safeward-ui.settings.v2'

const DEFAULTS: PersistedSettings = {
  safewardUrl: '',
  safewardAuthToken: '',
  // 0 = manual only. Auto-refresh caused the tx cards to repaint while
  // the user was reading a simulation, which was jarring. Kept as an
  // opt-in setting for users who want polling.
  autoRefreshSeconds: 0,
}

const ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/

export function loadSettings(): AppSettings {
  let persisted: PersistedSettings = DEFAULTS
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) {
      // Older versions of the schema stored `moduleSafe` here. mergeWithUrl
      // explicitly enumerates the fields it cares about, so any stray
      // `moduleSafe` from older localStorage is silently dropped.
      const parsed = JSON.parse(raw) as Partial<PersistedSettings>
      persisted = { ...DEFAULTS, ...parsed }
    }
  } catch {
    /* fall through to defaults */
  }
  return mergeWithUrl(persisted)
}

export function saveSettings(s: AppSettings): void {
  // Strip moduleSafe before persisting — Safe address is URL-only state.
  const persisted: PersistedSettings = {
    safewardUrl: s.safewardUrl,
    safewardAuthToken: s.safewardAuthToken,
    autoRefreshSeconds: s.autoRefreshSeconds,
  }
  localStorage.setItem(KEY, JSON.stringify(persisted))
}

/**
 * URL params override stored settings except for the auth token (never read
 * from the URL — keeps secrets out of browser history). The Safe address
 * comes ONLY from the URL — bare URL means no Safe selected → landing page.
 */
function mergeWithUrl(s: PersistedSettings): AppSettings {
  if (typeof window === 'undefined') {
    return { ...s, moduleSafe: '' }
  }
  const params = new URLSearchParams(window.location.search)
  const safewardUrl = params.get('safeward')
  const refresh = params.get('refresh')
  const safe = params.get('safe')
  return {
    safewardUrl: safewardUrl ?? s.safewardUrl,
    safewardAuthToken: s.safewardAuthToken,
    autoRefreshSeconds: parseRefreshSeconds(refresh) ?? s.autoRefreshSeconds,
    moduleSafe: safe && ADDRESS_RE.test(safe) ? safe : '',
  }
}

/**
 * `?refresh=` accepts a positive integer seconds value. Anything else
 * (NaN, negative, sub-second) falls back to the stored default — without
 * this guard `?refresh=garbage` produced NaN which `setInterval` clamps
 * to 4ms, hammering /v1/health and /v1/pending until tab close.
 */
function parseRefreshSeconds(raw: string | null): number | null {
  if (raw == null) return null
  const n = Number(raw)
  if (!Number.isFinite(n) || n < 1) return null
  return n
}
